import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Typography,
    Collapse,
    Chip,
    styled,
    useTheme,
    Tooltip
} from '@mui/material';
import { tableCellClasses } from '@mui/material/TableCell';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Archive, Edit } from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import LoadingIndicator from './LoadingIndicator';
import PlanoAcaoResultadoConsulta from '../types/negocio/PlanoAcaoResultadoConsulta';
import { calcularPercentualConclusao, formatarPercentual } from '../utils/planoAcaoUtils';

const getPrioridadeColor = (prioridade: string): "error" | "warning" | "info" | "success" | "default" => {
    switch (prioridade) {
        case 'Muito alta': return 'error';
        case 'Alta': return 'warning';
        case 'Média': return 'info';
        case 'Baixa': return 'success';
        case 'Muito baixa': return 'default';
        default: return 'default';
    }
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        verticalAlign: 'top',
    },
}));

function Row({ plano, onArquivar, onEditar }: { plano: PlanoAcaoResultadoConsulta; onArquivar: (guid: string) => void; onEditar: (guid: string) => void }) {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const prazoFormatado = plano.prazoConclusao ? format(new Date(plano.prazoConclusao), 'dd/MM/yyyy', { locale: ptBR }) : '';
    const dataInicioFormatada = plano.dataInicio ? format(new Date(plano.dataInicio), 'dd/MM/yyyy', { locale: ptBR }) : '-';
    const percentualConclusao = calcularPercentualConclusao(plano);

    return (
        <React.Fragment>
            <TableRow>
                <StyledTableCell>
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </StyledTableCell>
                <StyledTableCell>{plano.acao}</StyledTableCell>
                <StyledTableCell>
                    {plano.prioridade ? (
                        <Chip 
                            label={plano.prioridade}
                            size="small"
                            color={getPrioridadeColor(plano.prioridade)}
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                        />
                    ) : '-'}
                </StyledTableCell>
                <StyledTableCell>
                    <Tooltip 
                        title={plano.totalTarefas !== undefined ? `${plano.tarefasFinalizadas ?? 0}/${plano.totalTarefas} tarefas concluídas` : ''}
                        arrow
                    >
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontWeight: 'bold',
                                color: percentualConclusao === 100 ? 'success.main' : 
                                       percentualConclusao >= 70 ? 'info.main' : 
                                       percentualConclusao >= 30 ? 'warning.main' : 'text.primary'
                            }}
                        >
                            {formatarPercentual(percentualConclusao)}
                        </Typography>
                    </Tooltip>
                </StyledTableCell>
                <StyledTableCell>{prazoFormatado || '-'}</StyledTableCell>
                <StyledTableCell>{plano.area && plano.metaGlobalDescricao ? `${plano.area}: ${plano.metaGlobalDescricao}` : plano.metaGlobalDescricao ?? '-'}</StyledTableCell>
                <StyledTableCell>{plano.fatorExternoDescricao ?? '-'}</StyledTableCell>
                <StyledTableCell>{plano.fatorInternoDescricao ?? '-'}</StyledTableCell>
            </TableRow>

            <TableRow>
                <TableCell colSpan={8} sx={{ p: 0 }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box
                            sx={{
                                px: 3,
                                py: 3,
                                backgroundColor: theme.palette.mode === 'light' ? '#f9f9f9' : theme.palette.background.paper,
                                borderTop: `1px solid ${theme.palette.divider}`,
                                display: 'flex',
                                gap: { xs: 2, sm: 8 },
                                flexWrap: 'wrap',
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom sx={{ fontWeight: 'bold' }}
                                >
                                    Mais detalhes
                                </Typography>

                                <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 }, flexWrap: 'wrap' }}>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>Estratégia</Typography>
                                        <Typography variant="body2">{plano.estrategia ?? '-'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>Responsável</Typography>
                                        <Typography variant="body2">{plano.responsavelNome ?? plano.responsavelGuid}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>Data de Início</Typography>
                                        <Typography variant="body2">{dataInicioFormatada}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>Status</Typography>
                                        <Typography variant="body2">{plano.status ?? '-'}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Mais opções
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 3, mt: 2.5 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                color: theme.palette.secondary.main
                                            }
                                        }}
                                        onClick={() => plano.guid && onArquivar(plano.guid)}
                                    >
                                        <Archive fontSize="small" />
                                        <Typography variant="body2">Arquivar</Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                color: theme.palette.secondary.main
                                            }
                                        }}
                                        onClick={() => plano.guid && onEditar(plano.guid)}
                                    >
                                        <Edit fontSize="small" />
                                        <Typography variant="body2">Editar</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

interface TabelaPlanoDeAcaoProps {
    readonly planos: PlanoAcaoResultadoConsulta[];
    readonly loading: boolean;
    readonly onArquivar: (guid: string) => void;
    readonly onEditar: (guid: string) => void;
}

const TabelaPlanoDeAcao: React.FC<TabelaPlanoDeAcaoProps> = ({ planos, loading, onArquivar, onEditar }) => {
    const theme = useTheme();

    return (
        <Box>
            <Paper
                elevation={3}
                sx={{
                    borderRadius: '8px',
                    boxShadow: theme.shadows[2],
                    overflow: 'hidden',
                    backgroundColor: theme.palette.background.paper
                }}
            >
                <TableContainer>
                    <Table stickyHeader sx={{ minWidth: 800 }}>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell />
                                <StyledTableCell>Ação</StyledTableCell>
                                <StyledTableCell>Prioridade</StyledTableCell>
                                <StyledTableCell>Progresso</StyledTableCell>
                                <StyledTableCell>Prazo</StyledTableCell>
                                <StyledTableCell>Meta Global</StyledTableCell>
                                <StyledTableCell>Fator Externo</StyledTableCell>
                                <StyledTableCell>Fator Interno</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <LoadingIndicator message="Carregando Planos de Ação..." />
                                    </TableCell>
                                </TableRow>
                            ) : planos.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="body1">Nenhum plano de ação encontrado.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {
                                        planos.map((plano, index) => (
                                            <Row key={plano.guid ?? index} plano={plano} onArquivar={onArquivar} onEditar={onEditar} />
                                        ))
                                    }
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default TabelaPlanoDeAcao;
